import * as Sentry from "@sentry/react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";

export type FormSelectValue = string | number;

export interface Option<T = FormSelectValue> {
  name: string;
  value: T;
}

export type FormSelectProps<TFormValues extends FieldValues> = Partial<
  Omit<AutocompleteProps<Option, false, false, false>, "name" | "multiple" | "freeSolo" | "defaultValue">
> & {
  name: Path<TFormValues>;
  options?: Option[];
  required?: TextFieldProps["required"];
  label?: TextFieldProps["label"];
  helperText?: TextFieldProps["helperText"];
};

export function FormSelect<TFormValues extends FieldValues>({
  name,
  options = [],
  label = "",
  required = false,
  disableClearable = false,
  ...props
}: FormSelectProps<TFormValues>) {
  const { control, setValue } = useFormContext<TFormValues>();

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <Autocomplete<Option, false, false, false>
              value={options.find((option) => option.value === field.value) ?? null}
              options={options}
              getOptionLabel={(option) => option.name ?? ""}
              fullWidth
              disableClearable={disableClearable}
              onChange={(event, value) => {
                setValue(field.name, (!value ? null : value.value) as PathValue<TFormValues, Path<TFormValues>>);
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    name={field.name}
                    // error={!!(meta.error && meta.touched)}
                    // helperText={meta.error && meta.touched ? meta.error : helperText}
                    label={label}
                    required={required} // Only used for the "*" that MUI adds
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off",
                    }}
                  />
                );
              }}
              {...props}
            />
          );
        }}
      />
    </Sentry.ErrorBoundary>
  );
}
